/**
 * Quantity offers on the PDP. See snippets/product-quantity-offers.liquid.
 *
 * The radios alone already work: they post "quantity" alongside the form's "id". This script adds what a single id
 * cannot express, a cart of different variants (mixed scents, or the bundle product instead of this one). It keeps a
 * set of items[] inputs attached to the product form in sync with the selection, and disables the form's "id" input
 * while it does, because /cart/add.js ignores items[] whenever "id" is present.
 *
 * A mix tier wants a scent for every extra jar. Adding to cart with a jar still open (from the form or the sticky bar)
 * does not add anything: the page scrolls to the selected tier and the open jars light up. The prices meanwhile count
 * an open jar as this product.
 *
 * With "automatic refills" ticked every jar goes in with the subscription plan (items[][selling_plan]) and the cards
 * show the plan's prices; refill jars count towards the free gift as one-time jars do. A jar without that plan (a mixed scent) or the bundle turns the box off until the selection
 * allows it again. The price row under the product title (data-offer-price-row) shows the same selected total and
 * its compare at price. From data-subscription-multi-from jars the cheaper second plan (data-selling-plan-multi) takes over,
 * for every jar of the tier; the refills title then shows that tier's own saving. The selected card's price is mirrored in the add to cart button (data-offer-price, see custom.css).
 */

function formatMoney(cents) {
  const format = window.themeVariables?.settings?.moneyFormat || '€{{amount_with_comma_separator}}';

  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, placeholder) => {
    const [integer, decimals] = (cents / 100).toFixed(2).split('.');

    switch (placeholder) {
      case 'amount':
        return `${integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${decimals}`;
      case 'amount_no_decimals':
        return Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      case 'amount_no_decimals_with_comma_separator':
        return Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      default:
        return `${integer.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimals}`;
    }
  });
}

const EASE_OUT = 'cubic-bezier(0.2, 0.7, 0.2, 1)';
const OPEN_PICKER = '.quantity-offer__picker:not([data-closing])';

// The link's tier is applied once per page: a product rerender (another scent) keeps the customer's own choice
let urlTierApplied = false;

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** "€44,00" becomes "€44": the badge's saving is in whole euros */
const formatWholeMoney = (cents) => formatMoney(Math.floor(cents / 100) * 100).replace(/[.,]00(?!\d)/, '');

const toCents = (value) => (value === undefined || value === '' ? null : Number(value));

class QuantityOffers extends HTMLElement {
  /** Scent picked per extra jar, by slot index (1 is the second jar). Shared across tiers. */
  #choices = new Map();
  #abortController;

  connectedCallback() {
    this.form = document.getElementById(this.getAttribute('form'));

    if (!this.form) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('change', (event) => {
      if (event.target.matches('.quantity-offer__input')) {
        // The card collapses together with its picker, so there is nothing to animate
        this.#closePicker({ animate: false });
        this.#sync();
      }

      if (event.target.matches('[data-offer-subscription-input]')) this.#sync();
    }, { signal });

    this.addEventListener('click', (event) => {
      const chooseButton = event.target.closest('[data-offer-choose]');
      const option = event.target.closest('[data-offer-option]');

      // event.detail is 0 for a click from the keyboard (Enter / Space): only then does focus move into the picker, so a
      // mouse or tap open does not put the browser's focus ring on the first scent
      if (chooseButton) this.#togglePicker(chooseButton, { fromKeyboard: event.detail === 0 });
      if (option) this.#pick(option);
    }, { signal });

    // Captured on the document, so it runs before the theme's own submit handler on the form and can stop it
    document.addEventListener('submit', (event) => {
      if (event.target !== this.form) return;

      const openSlots = this.#openSlots();

      if (openSlots.length === 0) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.#remind(openSlots);
    }, { capture: true, signal });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.querySelector(OPEN_PICKER)) {
        const button = this.#closePicker();
        button?.focus();
      }
    }, { signal });

    this.#selectFromUrl();
    this.#sync();
  }

  /**
   * A link can preselect a tier by its jar count, e.g. /products/the-original?jars=3 from an ad landing page's offer
   * card. Only a tier that exists is selected; anything else leaves the theme editor's default.
   */
  #selectFromUrl() {
    if (urlTierApplied) return;
    urlTierApplied = true;

    const jars = new URLSearchParams(window.location.search).get('jars');

    if (!jars || !/^\d+$/.test(jars)) return;

    const input = this.querySelector(`.quantity-offer__input[value="${jars}"]`);

    if (input) input.checked = true;
  }

  disconnectedCallback() {
    this.#abortController?.abort();

    // The product rerender can swap this element out; give the form its plain "id" back until the new one takes over
    const idInput = this.#idInput;

    if (idInput) idInput.disabled = false;

    const button = this.#submitButton;

    if (button) {
      delete button.dataset.offerPrice;
      button.querySelectorAll('.button__price').forEach((price) => price.remove());
    }
  }

  get #idInput() {
    return this.form?.querySelector('input[name="id"]');
  }

  get #selectedInput() {
    return this.querySelector('.quantity-offer__input:checked');
  }

  get #submitButton() {
    return this.form?.querySelector('button[type="submit"]');
  }

  get #subscriptionInput() {
    return this.querySelector('[data-offer-subscription-input]');
  }

  /** Ticked, and possible for the current selection */
  get #subscribing() {
    const input = this.#subscriptionInput;
    return Boolean(input?.checked && !input.disabled);
  }

  get #baseItem() {
    return {
      id: this.dataset.variantId,
      price: Number(this.dataset.price),
      comparePrice: Number(this.dataset.comparePrice),
      subscriptionPrice: toCents(this.dataset.subscriptionPrice),
      subscriptionMultiPrice: toCents(this.dataset.subscriptionMultiPrice),
    };
  }

  /** Whether a tier of this many jars uses the cheaper plan: all its jars must be on that plan too */
  #usesMultiPlan(quantity, items) {
    const from = Number(this.dataset.subscriptionMultiFrom) || 0;
    return from > 0 && quantity >= from && items.every((item) => item.subscriptionMultiPrice !== null);
  }

  #itemsForQuantity(quantity) {
    return Array.from({ length: quantity }, (_, index) => this.#itemForSlot(index));
  }

  #itemForSlot(index) {
    return index === 0 ? this.#baseItem : (this.#choices.get(index) ?? this.#baseItem);
  }

  /** The variants the selected card adds to the cart, one entry per jar */
  #selectedItems() {
    const input = this.#selectedInput;

    if (!input) return [];

    if (input.dataset.bundleVariantId) return [{ id: input.dataset.bundleVariantId }];

    return Array.from({ length: Number(input.value) }, (_, index) => this.#itemForSlot(index));
  }

  #sync() {
    this.#updateSubscription();
    this.#updatePrices();
    this.#writeInputs();
  }

  /** Refills need the plan on every jar of the selection; the bundle never has it */
  #updateSubscription() {
    const input = this.#subscriptionInput;

    if (!input) return;

    const selected = this.#selectedInput;
    const available = Boolean(selected) && !selected.dataset.bundleVariantId &&
      this.#selectedItems().every((item) => item.subscriptionPrice !== null);

    input.disabled = !available;

    const note = this.querySelector('[data-offer-subscription-unavailable]');
    if (note) note.hidden = available;
  }

  #updatePrices() {
    const subscribing = this.#subscribing;
    let selectedTotal = null;
    let selectedCompare = null;

    this.querySelectorAll('[data-offer-tier]').forEach((tier) => {
      const input = tier.querySelector('.quantity-offer__input');
      const quantity = Number(input.value);
      const discount = Number(input.dataset.discount) || 0;

      let total = 0;
      let compareTotal = 0;
      const multi = this.#usesMultiPlan(quantity, this.#itemsForQuantity(quantity));

      for (let index = 0; index < quantity; index++) {
        const item = this.#itemForSlot(index);

        if (subscribing && multi) {
          total += item.subscriptionMultiPrice;
        } else if (subscribing && item.subscriptionPrice !== null) {
          // The plan's own price. The tier discount is left out: an automatic discount only reaches subscription
          // lines when it is set up for them, so counting it here could show less than the cart charges.
          total += item.subscriptionPrice;
        } else {
          // Per unit and rounded down to the cent, as Shopify does, so the card shows the exact cart price
          total += item.price - Math.floor((item.price * discount) / 100);
        }

        compareTotal += Math.max(item.comparePrice, item.price);
      }

      const priceElement = tier.querySelector('[data-offer-price]');
      const compareElement = tier.querySelector('[data-offer-compare-price]');

      priceElement.textContent = formatMoney(total);
      compareElement.textContent = formatMoney(compareTotal);
      compareElement.hidden = compareTotal <= total;

      let extras = Number(tier.dataset.extrasValue) || 0;

      // Free shipping as the cart gives it: the threshold for a one time purchase, the plan's own free shipping for
      // refills (on the plan this tier gets)
      const shipping = tier.querySelector('[data-offer-shipping]');

      if (shipping) {
        const free = subscribing
          ? shipping.dataset[multi ? 'refillsMulti' : 'refillsSingle'] === 'true'
          : shipping.dataset.oneTime === 'true';

        shipping.hidden = !free;
        if (free) extras += Number(shipping.dataset.value) || 0;
      }

      const extrasRow = tier.querySelector('[data-offer-extras]');

      if (extrasRow) {
        const anyExtra = Array.from(extrasRow.children).some((extra) => !extra.hidden);
        extrasRow.hidden = !anyExtra;
        tier.classList.toggle('quantity-offer--has-extras', anyExtra);
      }

      this.#updateBadge(tier, compareTotal - total + extras);

      if (input.checked) {
        selectedTotal = total;
        selectedCompare = compareTotal;
      }
    });

    const selected = this.#selectedInput;

    if (selected?.dataset.bundleVariantId) {
      selectedTotal = Number(selected.dataset.price);
      selectedCompare = Number(selected.dataset.comparePrice) || 0;
    }

    if (selectedTotal !== null) {
      this.#updateButtonPrice(formatMoney(selectedTotal));
      this.#updatePriceRow(selectedTotal, selectedCompare);
    }

    this.#updateHighlight();
    this.#updateFrequency();
  }

  /**
   * "Refills every 45 days" in the perks: when the plan from 2 jars delivers at another interval (the Sensitive Skin
   * duo every 90 days), the interval follows the plan the selected tier would get
   */
  #updateFrequency() {
    const selected = this.#selectedInput;

    if (!selected || selected.dataset.bundleVariantId) return;

    const quantity = Number(selected.value);
    const multi = this.#usesMultiPlan(quantity, this.#itemsForQuantity(quantity));

    this.querySelectorAll('[data-offer-frequency][data-multi]').forEach((element) => {
      element.textContent = multi ? element.dataset.multi : element.dataset.single;
    });
  }

  /**
   * The price under the product title and the one in the sticky add to cart follow the selection: the offer's total,
   * struck through its compare at total
   */
  #updatePriceRow(total, compareTotal) {
    const row = this.closest('.product-info')?.querySelector('[data-offer-price-row]');
    // The form's ID from its attribute: form.id would be its input named "id"
    const formId = this.getAttribute('form');
    const stickyPrices = document.querySelectorAll(`product-sticky-add-to-cart[main-product-form="${formId}"] price-list`);

    [row, ...stickyPrices].forEach((priceList) => this.#writePrices(priceList, total, compareTotal));
  }

  #writePrices(priceList, total, compareTotal) {
    const salePrice = priceList?.querySelector('sale-price');

    if (!salePrice) return;

    // Keep the screen reader label ("Sale price") and swap only the amount after it
    const setAmount = (element, cents) => {
      element.childNodes.forEach((node) => node.nodeType === Node.TEXT_NODE && node.remove());
      element.append(formatMoney(cents));
    };

    setAmount(salePrice, total);

    let comparePrice = priceList.querySelector('compare-at-price');

    if (compareTotal > total) {
      if (!comparePrice) {
        comparePrice = document.createElement('compare-at-price');
        // The sticky bar sets its prices in the subheading style
        comparePrice.className = salePrice.classList.contains('subheading') ? 'subheading text-subdued line-through' : 'text-subdued line-through';
        salePrice.after(comparePrice);
      }

      setAmount(comparePrice, compareTotal);
      comparePrice.hidden = false;
    } else if (comparePrice) {
      comparePrice.hidden = true;
    }
  }

  /**
   * The price goes inside the button's content, so the theme's hover band carries it along with "ADD TO CART"; the
   * second copy rides in the band's duplicate that slides in from the left (see custom.css).
   */
  #updateButtonPrice(text) {
    const button = this.#submitButton;
    const content = button?.querySelector('.button__content');

    if (!button || !content) return;

    button.dataset.offerPrice = text;

    [false, true].forEach((copy) => {
      let price = content.querySelector(copy ? '.button__price--copy' : '.button__price:not(.button__price--copy)');

      if (!price) {
        price = document.createElement('span');
        price.className = copy ? 'button__price button__price--copy' : 'button__price';
        if (copy) price.setAttribute('aria-hidden', 'true');
        content.append(price);
      }

      price.textContent = text;
    });
  }

  /** "Save 24%" for one jar, "Save 33%" for a tier on the cheaper plan: the saving of the selected tier's first jar */
  #updateHighlight() {
    const highlight = this.querySelector('[data-offer-subscription-highlight]');
    const selected = this.#selectedInput;

    if (!highlight?.dataset.template?.includes('[discount]') || !selected || selected.dataset.bundleVariantId) return;

    const quantity = Number(selected.value);
    const item = this.#baseItem;
    const price = this.#usesMultiPlan(quantity, this.#itemsForQuantity(quantity)) ? item.subscriptionMultiPrice : item.subscriptionPrice;

    if (price === null || !item.price) return;

    highlight.textContent = highlight.dataset.template.replace('[discount]', Math.max(0, Math.floor(((item.price - price) * 100) / item.price)));
  }

  #updateBadge(tier, saving) {
    const badge = tier.querySelector('[data-offer-badge]');
    const template = badge?.dataset.template;

    if (!template?.includes('[saving]')) return;

    badge.textContent = template.replace('[saving]', formatWholeMoney(saving));
    badge.hidden = saving < 100;
  }

  #writeInputs() {
    const container = this.querySelector('[data-offer-inputs]');
    const idInput = this.#idInput;
    const quantities = new Map();
    const items = this.#selectedItems();
    const refills = this.querySelector('[data-offer-subscription]');
    let sellingPlan = null;

    if (this.#subscribing) {
      sellingPlan = this.#usesMultiPlan(items.length, items) ? refills.dataset.sellingPlanMulti : refills.dataset.sellingPlan;
    }

    items.forEach(({ id }) => quantities.set(id, (quantities.get(id) ?? 0) + 1));

    // The plain id + quantity post already covers "n of this product"; only step in when that is not the selection,
    // or when the jars go in with the refills plan
    const needsItems = Boolean(sellingPlan) || quantities.size > 1 ||
      (quantities.size === 1 && !quantities.has(this.dataset.variantId));

    container.replaceChildren();

    if (idInput) idInput.disabled = needsItems;

    if (!needsItems) return;

    let index = 0;

    quantities.forEach((quantity, id) => {
      container.append(
        this.#hiddenInput(`items[${index}][id]`, id),
        this.#hiddenInput(`items[${index}][quantity]`, quantity)
      );

      if (sellingPlan) container.append(this.#hiddenInput(`items[${index}][selling_plan]`, sellingPlan));

      index++;
    });
  }

  #hiddenInput(name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    // Not this.form.id: on a form holding an input named "id", that property returns the input, not the form's id
    input.setAttribute('form', this.getAttribute('form'));
    return input;
  }

  #togglePicker(button, { fromKeyboard = false } = {}) {
    const isOpen = button.getAttribute('aria-expanded') === 'true';
    this.#closePicker();

    if (isOpen) return;

    const template = this.querySelector('template[data-offer-picker]');

    if (!template) return;

    const slot = button.closest('[data-offer-slot]');
    const picker = template.content.firstElementChild.cloneNode(true);
    const current = this.#choices.get(Number(slot.dataset.offerSlot));

    picker.querySelectorAll('[data-offer-option]').forEach((option) => {
      option.setAttribute('aria-pressed', String(option.dataset.variantId === current?.id));
    });

    slot.after(picker);
    button.setAttribute('aria-expanded', 'true');
    if (fromKeyboard) {
      picker.querySelector('[aria-pressed="true"], [data-offer-option]')?.focus({ preventScroll: true });
    }

    this.#animatePicker(picker, 'open');
  }

  /** Closes the open picker, returning the button that opened it */
  #closePicker({ animate = true } = {}) {
    const button = this.querySelector('[data-offer-choose][aria-expanded="true"]');
    const picker = this.querySelector(OPEN_PICKER);

    button?.setAttribute('aria-expanded', 'false');

    if (picker) {
      if (animate) {
        // Marked so a picker opened meanwhile is not mistaken for this one while it slides away
        picker.dataset.closing = '';
        this.#animatePicker(picker, 'close').then(() => picker.remove());

        // Animations pause in background tabs, so "finished" may never come; the picker must still go
        setTimeout(() => picker.remove(), 400);
      } else {
        picker.remove();
      }
    }

    return button;
  }

  /**
   * Slides the picker open or shut by animating its height, so the rows below move instead of jumping. The negative
   * margin cancels the grid gap above the picker, which would otherwise pop in before the height starts growing.
   */
  async #animatePicker(picker, direction) {
    if (prefersReducedMotion()) return;

    const { marginTop } = getComputedStyle(picker);
    const parentGap = parseFloat(getComputedStyle(picker.parentElement).rowGap) || 0;
    const open = { height: `${picker.offsetHeight}px`, marginTop, opacity: 1 };
    const closed = { height: '0px', marginTop: `${parseFloat(marginTop) - parentGap}px`, opacity: 0 };

    picker.style.overflow = 'hidden';

    const animation = picker.animate(direction === 'open' ? [closed, open] : [open, closed], {
      duration: direction === 'open' ? 280 : 200,
      easing: direction === 'open' ? EASE_OUT : 'ease-in',
      fill: direction === 'open' ? 'none' : 'forwards',
    });

    if (direction === 'open') {
      picker.querySelectorAll('[data-offer-option]').forEach((option, index) => {
        option.animate([{ opacity: 0, transform: 'translateY(6px)' }, { opacity: 1, transform: 'none' }], {
          duration: 220,
          delay: 60 + index * 30,
          easing: EASE_OUT,
          fill: 'backwards',
        });
      });
    }

    try {
      await animation.finished;
    } catch {
      // Cancelled because the picker was removed mid-way; nothing left to clean up
    }

    picker.style.overflow = '';
  }

  /** Extra jars in the selected tier the customer has not chosen a scent for yet */
  #openSlots() {
    const tier = this.#selectedInput?.closest('[data-offer-tier]');

    if (!tier) return [];

    return Array.from(tier.querySelectorAll('[data-offer-slot]')).filter(
      (slot) => slot.querySelector('[data-offer-choose]') && !this.#choices.has(Number(slot.dataset.offerSlot))
    );
  }

  /** Brings the selected tier into view and makes its open jars pulse, so it is clear what is still missing */
  #remind(slots) {
    this.#closePicker({ animate: false });

    const tier = slots[0].closest('[data-offer-tier]');
    tier.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });

    slots.forEach((slot) => {
      // Restarts the pulse when the customer clicks add to cart again while it is still running
      slot.classList.remove('is-attention');
      void slot.offsetWidth;
      slot.classList.add('is-attention');
    });

    slots[0].querySelector('[data-offer-choose]')?.focus({ preventScroll: true });
  }

  #pick(option) {
    const button = this.querySelector('[data-offer-choose][aria-expanded="true"]');
    const index = Number(button?.closest('[data-offer-slot]')?.dataset.offerSlot);

    if (!index) return;

    const { variantId, price, comparePrice, subscriptionPrice, subscriptionMultiPrice, name, type, image } = option.dataset;
    this.#choices.set(index, {
      id: variantId,
      price: Number(price),
      comparePrice: Number(comparePrice),
      subscriptionPrice: toCents(subscriptionPrice),
      subscriptionMultiPrice: toCents(subscriptionMultiPrice),
      name,
      type,
      image,
    });

    // The same jar position exists in more than one tier, so every copy of this slot shows the pick
    this.querySelectorAll(`[data-offer-slot="${index}"]`).forEach((slot) => {
      slot.classList.remove('is-attention');
      this.#renderSlot(slot, index);
    });

    this.#closePicker();
    button.focus();
    this.#sync();
  }

  #renderSlot(slot, index) {
    const choice = this.#choices.get(index);

    if (!choice) return;

    const image = slot.querySelector('[data-offer-slot-image]');
    const chooseButton = slot.querySelector('[data-offer-choose]');

    if (choice.image) {
      image.src = choice.image;
      image.removeAttribute('srcset');
    }

    slot.querySelector('[data-offer-slot-type]').textContent = choice.type;
    slot.querySelector('[data-offer-slot-name]').textContent = choice.name;
    slot.querySelector('[data-offer-slot-text]').hidden = false;

    if (chooseButton.dataset.changeLabel) chooseButton.textContent = chooseButton.dataset.changeLabel;

    // Only the visible copy of the slot gets the "drop in" effect; the others update silently
    if (!prefersReducedMotion() && slot.offsetParent) {
      image.animate([{ opacity: 0, transform: 'scale(0.6)' }, { opacity: 1, transform: 'none' }], {
        duration: 320,
        easing: 'cubic-bezier(0.3, 1.4, 0.5, 1)',
      });

      slot.querySelector('[data-offer-slot-text]').animate(
        [{ opacity: 0, transform: 'translateX(-6px)' }, { opacity: 1, transform: 'none' }],
        { duration: 240, delay: 60, easing: EASE_OUT, fill: 'backwards' }
      );
    }
  }
}

if (!window.customElements.get('quantity-offers')) {
  window.customElements.define('quantity-offers', QuantityOffers);
}
